import { Controller } from "stimulus";
import { logEvent } from "utils/analytics";

export default class FeedbackPrompt extends Controller {
  static targets = [
    "pill",
    "panel",
    "promptState",
    "issuesState",
    "thanksGoodState",
    "thanksBadState",
    "tag",
    "note",
    "disclosure",
    "error",
    "submitButton",
  ];

  static values = {
    delay: Number,
    capKey: String,
    createUrl: String,
    context: String,
    subjectType: String,
    subjectId: Number,
  };

  connect() {
    this.sentiment = null;
    this.expanded = false;
    // Remove rather than just return: a Turbo cache restore can bring back an already-opened
    // card with a fresh controller whose sentiment is null, and submitting that fails validation.
    if (this.alreadyPrompted()) {
      this.element.remove();
      return;
    }

    this.followBottomBar();

    if (this.delayValue > 0) {
      this.revealTimer = setTimeout(() => this.reveal(), this.delayValue * 1000);
    } else {
      this.reveal();
    }
  }

  disconnect() {
    if (this.revealTimer) clearTimeout(this.revealTimer);
    this.revealTimer = null;
    this.bottomBarObserver?.disconnect();
    this.bottomBarObserver = null;
  }

  // Keeps the docked pill clear of the page's bottom bar (the tab bar and mini player). Its height
  // changes as the mini player opens and closes, and the tab bar only shows below md.
  followBottomBar() {
    const bar = document.querySelector("[data-bottom-bar]");
    if (!bar || typeof ResizeObserver === "undefined") return;

    this.bottomBarObserver = new ResizeObserver(() => {
      this.element.style.setProperty("--feedback-prompt-lift", `${bar.offsetHeight}px`);
    });
    this.bottomBarObserver.observe(bar);
  }

  // Shows the collapsed pill. It doesn't count towards the once-per-tab cap: only opening the card or
  // closing the prompt does, so a pill nobody touched comes back on the next page.
  reveal() {
    this.revealTimer = null;
    this.element.classList.remove("hidden");
    this.log("feedback_prompt_pill_shown");
  }

  // Opens the pill into the card and moves focus to its first control, the happiest face.
  // feedback_prompt_shown keeps its meaning from before the pill: the card was shown.
  expand() {
    this.expanded = true;
    this.pillTarget.classList.add("hidden");
    this.panelTarget.classList.remove("hidden");
    this.panelTarget.querySelector("button")?.focus();
    this.markPrompted();
    this.log("feedback_prompt_shown");
  }

  async chooseSentiment(event) {
    this.sentiment = event.currentTarget.dataset.sentiment;
    this.log("feedback_prompt_sentiment", { sentiment: this.sentiment });

    if (this.sentiment !== "good") {
      this.showState(this.issuesStateTarget);
      return;
    }

    // A bare "good" is the whole report, so submit it now and only say thanks once it's saved.
    try {
      await this.post();
      this.showState(this.thanksGoodStateTarget);
      this.log("feedback_prompt_submitted", { sentiment: this.sentiment, tag_count: 0 });
    } catch {
      if (window.showToast) window.showToast("We couldn't send that. Try again in a moment.");
    }
  }

  toggleDisclosure(event) {
    const button = event.currentTarget;
    const expanded = button.getAttribute("aria-expanded") === "true";
    button.setAttribute("aria-expanded", String(!expanded));
    this.disclosureTarget.classList.toggle("hidden", expanded);
  }

  // From the pill or the card; `expanded` tells the two apart.
  dismiss() {
    this.markPrompted();
    this.log("feedback_prompt_dismissed", { expanded: this.expanded });
    this.element.remove();
  }

  showState(target) {
    [this.promptStateTarget, this.issuesStateTarget, this.thanksGoodStateTarget, this.thanksBadStateTarget]
      .forEach((state) => state.classList.toggle("hidden", state !== target));
  }

  async submit() {
    if (this.inFlight) return;
    this.inFlight = true;
    this.setSubmitDisabled(true);
    this.clearError();

    try {
      await this.post();
      // Never log the note text itself.
      this.log("feedback_prompt_submitted", {
        sentiment: this.sentiment,
        tag_count: this.selectedTags().length,
        has_note: this.noteText().length > 0,
      });
      this.showState(this.thanksBadStateTarget);
    } catch {
      this.showError("We couldn't send that. Try again in a moment.");
      if (window.showToast) window.showToast("We couldn't send that. Try again in a moment.");
    } finally {
      this.inFlight = false;
      this.setSubmitDisabled(false);
    }
  }

  // Checks the content type so a login redirect (a 200 HTML page) throws instead of failing in
  // response.json().
  async post() {
    const response = await fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({
        context: this.contextValue,
        sentiment: this.sentiment,
        tags: this.selectedTags(),
        note: this.noteText(),
        subject_type: this.subjectTypeValue || null,
        subject_id: this.subjectIdValue || null,
        page_url: window.location.href,
      }),
    });

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) throw new Error("non_json_response");
    if (!response.ok) throw new Error("request_failed");

    return response.json();
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }

  selectedTags() {
    return this.tagTargets.filter((tag) => tag.checked).map((tag) => tag.value);
  }

  noteText() {
    return this.hasNoteTarget ? this.noteTarget.value.trim() : "";
  }

  setSubmitDisabled(disabled) {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = disabled;
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  clearError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }

  alreadyPrompted() {
    try {
      return window.sessionStorage.getItem(this.capKeyValue) === "1";
    } catch {
      // Private-mode / storage-disabled browsers: no cap rather than no prompt.
      return false;
    }
  }

  markPrompted() {
    try {
      window.sessionStorage.setItem(this.capKeyValue, "1");
    } catch {
      // Ignore - see alreadyPrompted.
    }
  }

  log(name, properties = {}) {
    logEvent(name, {
      context: this.contextValue,
      subject_type: this.subjectTypeValue || null,
      subject_id: this.subjectIdValue || null,
      ...properties,
    });
  }
}
